import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator,
} from "./ui/command";
import { Users, FileText, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Global Cmd-K / Ctrl-K command palette for the admin shell.
 * Searches merchants, claims, and policies via /api/admin/search and
 * navigates to the corresponding detail page on select.
 */
export default function CmdKPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ merchants: [], claims: [], policies: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timeoutRef = useRef(null);

  useEffect(() => {
    function onKey(e) {
      const isMac = navigator.platform?.toUpperCase().includes("MAC");
      const hot = isMac ? e.metaKey : e.ctrlKey;
      if (hot && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    function onOpenEvent() { setOpen(true); }
    window.addEventListener("keydown", onKey);
    window.addEventListener("nyk:open-cmdk", onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("nyk:open-cmdk", onOpenEvent);
    };
  }, [open]);

  const fetchSearch = useCallback((q) => {
    if (!q || q.length < 2) {
      setResults({ merchants: [], claims: [], policies: [] });
      return;
    }
    setLoading(true);
    api.get("/admin/search", { params: { q } })
      .then((r) => setResults(r.data))
      .catch(() => setResults({ merchants: [], claims: [], policies: [] }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => fetchSearch(query), 220);
    return () => timeoutRef.current && clearTimeout(timeoutRef.current);
  }, [query, fetchSearch]);

  function go(path) {
    setOpen(false);
    setQuery("");
    navigate(path);
  }

  const hasAny =
    results.merchants.length + results.claims.length + results.policies.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Search merchants, claims, policies… (Cmd-K)"
        data-testid="cmdk-input"
      />
      <CommandList data-testid="cmdk-list">
        {loading && (
          <div className="flex items-center justify-center py-6 text-xs text-[#5c6360]" data-testid="cmdk-loading">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Searching…
          </div>
        )}
        {!loading && !hasAny && query.length >= 2 && (
          <CommandEmpty data-testid="cmdk-empty">No matches.</CommandEmpty>
        )}
        {results.merchants.length > 0 && (
          <CommandGroup heading="Merchants">
            {results.merchants.map((m) => (
              <CommandItem
                key={m.id}
                value={`merchant ${m.business_name} ${m.id}`}
                onSelect={() => go(`/merchants/${m.id}`)}
                data-testid={`cmdk-merchant-${m.id}`}
              >
                <Users className="w-4 h-4 mr-2 text-[#114232]" />
                <span className="font-medium">{m.business_name}</span>
                <span className="text-xs text-[#5c6360] ml-2 font-mono">{m.phone}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.claims.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Claims">
              {results.claims.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`claim ${c.claim_number} ${c.id}`}
                  onSelect={() => go(`/claims`)}
                  data-testid={`cmdk-claim-${c.id}`}
                >
                  <AlertTriangle className="w-4 h-4 mr-2 text-[#c15337]" />
                  <span className="font-mono text-xs">{c.claim_number}</span>
                  <span className="ml-2 text-xs text-[#5c6360]">
                    KES {c.amount_claimed?.toLocaleString()} · {c.status}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {results.policies.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Policies">
              {results.policies.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`policy ${p.policy_number} ${p.id}`}
                  onSelect={() => go(`/policies`)}
                  data-testid={`cmdk-policy-${p.id}`}
                >
                  <FileText className="w-4 h-4 mr-2 text-[#114232]" />
                  <span className="font-mono text-xs">{p.policy_number}</span>
                  <span className="ml-2 text-xs text-[#5c6360]">
                    {p.tier} · {p.status}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
